import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Ban, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog réutilisable pour exempter une opération de la FNE OU la ré-inclure.
 *
 * Props :
 * - open, onOpenChange
 * - operationId, operationLabel (pour le titre)
 * - mode : 'exempt' | 'reinclude'
 * - onSuccess (callback après save)
 */
export function FneExemptionDialog({
  open,
  onOpenChange,
  operationId,
  operationLabel,
  mode = 'exempt',
  onSuccess,
}) {
  const [reasons, setReasons] = useState({});
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setReasonDetail('');
    if (mode === 'exempt') {
      axios.get(`${API}/operations/fne-exemption/reasons`)
        .then((r) => setReasons(r.data?.reasons || {}))
        .catch(() => {});
    }
  }, [open, mode]);

  const submit = async () => {
    if (mode === 'exempt') {
      if (!reason) {
        toast.error('Motif obligatoire');
        return;
      }
      if (reason === 'other' && !reasonDetail.trim()) {
        toast.error('Détail obligatoire pour le motif "Autre"');
        return;
      }
    } else {
      if (!reasonDetail.trim()) {
        toast.error('Veuillez justifier la ré-inclusion');
        return;
      }
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/operations/${operationId}/fne-exemption`, {
        isExempt: mode === 'exempt',
        reason: mode === 'exempt' ? reason : null,
        reasonDetail: reasonDetail.trim() || null,
      });
      toast.success(mode === 'exempt' ? 'Opération exemptée FNE' : 'Opération ré-incluse dans la FNE');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error('Erreur', { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'exempt' ? (
              <><Ban className="w-5 h-5 text-amber-600" /> Exempter de la FNE</>
            ) : (
              <><RotateCcw className="w-5 h-5 text-emerald-600" /> Ré-inclure dans la FNE</>
            )}
          </DialogTitle>
          <DialogDescription>
            {mode === 'exempt'
              ? `L'opération ${operationLabel || ''} sera exclue de la certification DGI. Cette action est tracée pour audit OHADA.`
              : `L'opération ${operationLabel || ''} redeviendra éligible à la certification FNE.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {mode === 'exempt' && (
            <div>
              <Label className="text-xs font-medium">Motif d&apos;exemption <span className="text-rose-600">*</span></Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1 h-9" data-testid="fne-exemption-reason">
                  <SelectValue placeholder="Sélectionner un motif" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(reasons).map(([code, label]) => (
                    <SelectItem key={code} value={code}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-medium">
              {mode === 'exempt' ? (
                <>Détail / commentaire {reason === 'other' && <span className="text-rose-600">*</span>}</>
              ) : (
                <>Raison de la ré-inclusion <span className="text-rose-600">*</span></>
              )}
            </Label>
            <Textarea
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              placeholder={mode === 'exempt' ? 'Détails (optionnel sauf si "Autre")' : 'Justifiez la ré-inclusion (obligatoire — audit OHADA)'}
              className="mt-1 min-h-[80px] text-sm"
              data-testid="fne-exemption-detail"
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-600">
            <strong>Note OHADA</strong> : cette action sera enregistrée dans l&apos;audit log et notifiée à la comptabilité par email.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className={mode === 'exempt' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            data-testid="fne-exemption-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {mode === 'exempt' ? 'Exempter' : 'Ré-inclure'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FneExemptionDialog;
